import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@api/fast-games/generated';

import { ARRAY_EMPTY } from '@libs/constants';
import { ISocketEvents } from '@libs/socket';

import { FastGamesSocket, IFastGamesSocketEvents } from '../ws';
import { GET, POST } from './clients';

export const FASTGAMES_QUERY_KEYS = {
  all: () => ['FAST_GAMES'],
  pools: () => ['FAST_GAMES', 'POOLS'],
  session: (device: string, anonymousSecret: string | null) => [
    'FAST_GAMES',
    'SESSION',
    device,
    anonymousSecret,
  ],
  selectedPools: (device: string, anonymousSecret: string | null) => [
    'FAST_GAMES',
    'SESSION',
    device,
    anonymousSecret,
    'POOLS',
  ],
};

export function useFastGameSessionQuery(deviceId: string, anonymousSecret: string | null) {
  const qc = useQueryClient();

  return useQuery({
    queryKey: FASTGAMES_QUERY_KEYS.session(deviceId, anonymousSecret),
    queryFn: async () => {
      const data = (
        await GET('/api/subscription', {
          params: {
            query: { deviceId, anonymousSecret: anonymousSecret ?? undefined },
          },
        })
      ).data!;

      qc.setQueryData(
        FASTGAMES_QUERY_KEYS.selectedPools(deviceId, anonymousSecret),
        data.lastEntry.st === 'Queuing' ? data.lastEntry.ps : ARRAY_EMPTY,
      );

      return data;
    },
  });
}

export function useSelectedPoolsCache(
  deviceId: string,
  anonymousSecret: string | null,
): components['schemas']['PoolType'][] | undefined {
  const qc = useQueryClient();
  return qc.getQueryData(FASTGAMES_QUERY_KEYS.selectedPools(deviceId, anonymousSecret));
}

export function usePoolsQuery({
  enabled = true,
  staleTime,
}: { enabled?: boolean; staleTime?: number } = {}) {
  return useQuery({
    enabled,
    staleTime,
    queryKey: FASTGAMES_QUERY_KEYS.pools(),
    queryFn: async () => {
      return (await GET('/api/pools')).data!;
    },
  });
}

export function useJoinPoolsMutation(deviceId: string, anonymousSecret: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (selected: components['schemas']['PoolType'][]) => {
      qc.setQueryData(FASTGAMES_QUERY_KEYS.selectedPools(deviceId, anonymousSecret), selected);

      return (
        await POST('/api/subscription/pools', {
          body: { pools: selected },
          params: {
            query: { deviceId, anonymousSecret: anonymousSecret ?? undefined },
          },
        })
      ).data!;
    },
    onSuccess: (data) => {
      qc.setQueryData(
        FASTGAMES_QUERY_KEYS.session(deviceId, anonymousSecret),
        (prev: typeof data) => {
          const isNewer = isSessionNewer(prev, data);

          if (!isNewer) return prev;

          qc.setQueryData(
            FASTGAMES_QUERY_KEYS.selectedPools(deviceId, anonymousSecret),
            data.lastEntry.st === 'Queuing' ? data.lastEntry.ps : ARRAY_EMPTY,
          );

          return data;
        },
      );
    },
  });
}

export function useKeepAliveMutation(deviceId: string, anonymousSecret: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (req: components['schemas']['KeepPoolsRequest']) => {
      return (
        await POST('/api/subscription/pools/keep-alive', {
          body: req,
          params: {
            query: { deviceId, anonymousSecret: anonymousSecret ?? undefined },
          },
        })
      ).data!;
    },
    onSuccess: (data) => {
      qc.setQueryData(
        FASTGAMES_QUERY_KEYS.session(deviceId, anonymousSecret),
        (prev: typeof data) => {
          const isNewer = isSessionNewer(prev, data);
          if (!isNewer) return prev;

          qc.setQueryData(
            FASTGAMES_QUERY_KEYS.selectedPools(deviceId, anonymousSecret),
            data.lastEntry.st === 'Queuing' ? data.lastEntry.ps : ARRAY_EMPTY,
          );

          return data;
        },
      );
    },
  });
}

export function useDeviceAuthMutation(deviceId: string, anonymousSecret: string | null) {
  return useMutation({
    mutationFn: async (gameId: string) => {
      return (
        await POST('/api/device/auth', {
          params: {
            query: {
              deviceId,
              anonymousSecret: anonymousSecret ?? undefined,
              gameId,
            },
          },
        })
      ).data!;
    },
  });
}

export function useRejectGameMutation(deviceId: string, anonymousSecret: string | null) {
  return useMutation({
    mutationFn: async () => {
      await POST('/api/subscription/game/reject', {
        params: {
          query: { deviceId, anonymousSecret: anonymousSecret ?? undefined },
        },
      });
    },
  });
}

export function useRematchMutation(deviceId: string, anonymousSecret: string | null) {
  return useMutation({
    mutationFn: async () => {
      await POST('/api/subscription/game/rematch', {
        params: {
          query: { deviceId, anonymousSecret: anonymousSecret ?? undefined },
        },
      });
    },
  });
}

export function useFastGamesSocket({
  deviceId,
  anonymousId,
  anonymousSecret,
  enabled = true,
  cb,
}: {
  deviceId: string;
  // нужен только для кэширования
  anonymousId?: string | null;
  anonymousSecret: string | null;
  enabled?: boolean;
  cb?: (e: ISocketEvents<IFastGamesSocketEvents>) => void;
}) {
  const qc = useQueryClient();

  useFastGamesSocketInternal({
    deviceId,
    anonymousSecret,
    enabled,
    cb: (e) => {
      cb?.(e);

      if (e.$) return;
      switch (e.t) {
        case 'P':
          qc.setQueryData(
            FASTGAMES_QUERY_KEYS.pools(),
            (prev?: components['schemas']['PoolsDto']): components['schemas']['PoolsDto'] => {
              return {
                counters: prev ? { ...prev.counters, ...e.c } : e.c,
              };
            },
          );
          break;
        case 'S':
          qc.setQueryData(
            FASTGAMES_QUERY_KEYS.session(deviceId, anonymousSecret),
            (
              prev?: components['schemas']['SubscriptionDto'],
            ): components['schemas']['SubscriptionDto'] | undefined => {
              const next = {
                expiryMs: e.s.pse,
                gameRematchExpiryMs: e.s.gre ?? null,
                anonymousId: prev?.anonymousId ?? anonymousId,
                lastEntry: e.s,
                poolsExpiryMs: e.s.pse,
              };

              const isNewer = isSessionNewer(prev, next);

              if (!isNewer) return prev;

              qc.setQueryData(
                FASTGAMES_QUERY_KEYS.selectedPools(deviceId, anonymousSecret),
                e.s.st === 'Queuing' ? e.s.ps : ARRAY_EMPTY,
              );

              return next;
            },
          );
          break;
      }
    },
  });
}

function useFastGamesSocketInternal({
  deviceId,
  anonymousSecret,
  enabled = true,
  cb,
}: {
  deviceId: string;
  anonymousSecret: string | null;
  enabled?: boolean;
  cb: (e: ISocketEvents<IFastGamesSocketEvents>) => void;
}) {
  const socketRef = useRef<FastGamesSocket | null>(null);
  const cbRef = useRef(cb);
  cbRef.current = cb;

  if (!enabled) {
    socketRef.current?.discard();
    socketRef.current = null;
  } else if (
    !socketRef.current ||
    socketRef.current.deviceId !== deviceId ||
    socketRef.current.anonymousSecret !== anonymousSecret
  ) {
    socketRef.current?.discard();
    socketRef.current = new FastGamesSocket(deviceId, anonymousSecret);
    socketRef.current.subscribe((e) => cbRef.current(e));
  }

  useEffect(
    () => () => {
      socketRef.current?.discard();
    },
    [],
  );
}

function isSessionNewer(
  prev: components['schemas']['SubscriptionDto'] | undefined,
  next: components['schemas']['SubscriptionDto'],
): boolean {
  return !prev || !prev.lastEntry || prev.lastEntry.ts < next.lastEntry.ts;
}
